"use client";
import { useCallback, useEffect, useRef } from "react";
import gsap from "gsap";
import Image from "next/image";

const images = Array.from(
  { length: 40 },
  (_, i) => `/images/social-poster/frame${i + 1}.webp`
);

const MathUtils = {
  lerp: (a: number, b: number, n: number) => (1 - n) * a + n * b,
  distance: (x1: number, y1: number, x2: number, y2: number) =>
    Math.hypot(x2 - x1, y2 - y1),
};

export default function ImageTrail() {
  const imagesRef = useRef<(HTMLDivElement | null)[]>([]);
  const mousePos = useRef({ x: 0, y: 0 });
  const lastMousePos = useRef({ x: 0, y: 0 });
  const cacheMousePos = useRef({ x: 0, y: 0 });
  const imgPosition = useRef(0);
  const zIndexVal = useRef(1);
  const threshold = 50;

  const getMouseDistance = useCallback(
    () =>
      MathUtils.distance(
        mousePos.current.x,
        mousePos.current.y,
        lastMousePos.current.x,
        lastMousePos.current.y
      ),
    []
  );
  const showNextImage = useCallback(() => {
    const imgEl = imagesRef.current[imgPosition.current];
    if (!imgEl) return;

    const rect = imgEl.getBoundingClientRect();

    gsap.killTweensOf(imgEl);

    const directionX = mousePos.current.x - lastMousePos.current.x;

    const offsetX = directionX > 0 ? rect.width : -rect.width; // move opposite to direction

    gsap.set(imgEl, {
      opacity: 1,
      scale: 1,
      zIndex: zIndexVal.current,
      x: mousePos.current.x + offsetX, // offset in opposite direction
      y: mousePos.current.y - rect.height / 2,
    });

    gsap.to(imgEl, {
      duration: 1.5,
      ease: "power3.out",
      x: mousePos.current.x - rect.width / 2,
    });

    gsap.to(imgEl, {
      duration: 1,
      ease: "power1.out",
      opacity: 0,
      delay: 2,
    });

    gsap.to(imgEl, {
      duration: 2,
      ease: "quint.out",
      scale: 0.2,
      x: mousePos.current.x - offsetX, // continue past center in same direction
      delay: 1.5,
    });
  }, []);
  const render = useCallback(() => {
    const dist = getMouseDistance();

    cacheMousePos.current.x = MathUtils.lerp(
      cacheMousePos.current.x || mousePos.current.x,
      mousePos.current.x,
      0.1
    );
    cacheMousePos.current.y = MathUtils.lerp(
      cacheMousePos.current.y || mousePos.current.y,
      mousePos.current.y,
      0.1
    );

    if (dist > threshold) {
      showNextImage();
      zIndexVal.current++;
      imgPosition.current =
        imgPosition.current < images.length - 1 ? imgPosition.current + 1 : 0;
      lastMousePos.current = { ...mousePos.current };
    }

    requestAnimationFrame(render);
  }, [getMouseDistance, showNextImage]);
  useEffect(() => {
    const updateMouse = (e: MouseEvent) => {
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      mousePos.current = {
        x: e.clientX + scrollX,
        y: e.clientY + scrollY,
      };
    };

    window.addEventListener("mousemove", updateMouse);
    requestAnimationFrame(render);

    return () => window.removeEventListener("mousemove", updateMouse);
  }, [render]);

  return (
    <>
      <video
        width="1920"
        height="1080"
        preload="none"
        className="w-full h-screen fixed top-0 -z-10"
        autoPlay
        loop
        muted
        poster="/images/home.webp"
      >
        <source src="/video/home.webm" type="video/webm" />
        Your browser does not support the video tag.
      </video>
      {images.map((src, i) => (
        <div
          key={i}
          ref={(el) => {
            imagesRef.current[i] = el;
          }}
          className="fixed top-0 left-0 w-[200px] h-[200px] pointer-events-none opacity-0 origin-center will-change-transform -z-10"
        >
          <Image
            src={src}
            alt={`trail-${i}`}
            width={100}
            height={100}
            priority
          />
        </div>
      ))}
    </>
  );
}
